import { FC, useCallback } from "react";
import styled from "@emotion/styled";
import { Card, Flex, Select, Text } from "@radix-ui/themes";
import { useThemePreference } from "@/contexts/theme-preference";
import {
  THEME_PREFERENCES,
  toThemePreferenceLabel,
  isThemePreference,
} from "@/domains/styling/theme-preference";
import type { ThemePreference } from "@/domains/styling/theme-preference";

export const SettingsPage: FC = () => {
  const { preference, resolvedTheme, setPreference } = useThemePreference();

  const handlePreferenceChange = useCallback(
    (value: string) => {
      if (isThemePreference(value)) {
        setPreference(value);
      }
    },
    [setPreference]
  );

  return (
    <Container>
      <h1>Settings</h1>
      <Card>
        <Flex direction="column" gap="3">
          <SectionTitle>Appearance</SectionTitle>
          <Text size="2" color="gray">
            Choose how Atlas should look. “System” keeps things in sync with your OS preference.
          </Text>
          <SelectWrapper>
            <Text as="label" size="2" color="gray" htmlFor="theme-preference-select">
              Theme
            </Text>
            <Select.Root value={preference} onValueChange={handlePreferenceChange}>
              <Select.Trigger
                id="theme-preference-select"
                style={{ width: "220px" }}
                aria-label="Theme"
              />
              <Select.Content>
                {THEME_PREFERENCES.map(option => (
                  <Select.Item key={option} value={option}>
                    {toThemePreferenceLabel(option)}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <Text size="1" color="gray">
              Currently resolved to{" "}
              <Text as="span" color="gray" weight="bold">
                {capitalize(resolvedTheme)}
              </Text>
              .
            </Text>
          </SelectWrapper>
        </Flex>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
`;

const SelectWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const capitalize = (value: ThemePreference | "light" | "dark"): string =>
  value.charAt(0).toUpperCase() + value.slice(1);
